import React from "react";

import { faTrashCan } from "@fortawesome/pro-regular-svg-icons";
import { faGlobe } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { ButtonIcon, SingleSelectInput, Text, Tooltip } from "@performyard/py-components";

import {
	NO_EXPECTED_LEVEL_LABEL,
	levelSelectOptions,
	rankToSelectValue,
	selectValueToRank,
} from "../../../Competencies/utils";

interface CompetencyLite {
	_id: string;
	name: string;
	is_company_wide: boolean;
	levels: Array<{ rank: number; name: string }>;
}

export interface AddCompetencyRowProps {
	index: number;
	competency: CompetencyLite | undefined;
	expectedLevel: number | null;
	onChangeLevel: (level: number | null) => void;
	onRemove: () => void;
}

const AddCompetencyRow: React.FC<AddCompetencyRowProps> = ({
	index,
	competency,
	expectedLevel,
	onChangeLevel,
	onRemove,
}) => {
	// Stale-reference fallback. Reachable when a competency the form references was archived or
	// deleted between page load and now. Always allow removal.
	if (!competency) {
		return (
			<div data-testid={`competency-row-${index}`} className="flex w-full items-center justify-between gap-2 p-2">
				<Text size="paragraph-3" weight="regular" color="neutral-7">
					Competency unavailable — remove to continue
				</Text>
				<ButtonIcon
					variant="standard"
					color="neutral"
					shade={7}
					size={16}
					icon={faTrashCan}
					aria-label={`Remove competency ${index + 1}`}
					onClick={onRemove}
				/>
			</div>
		);
	}

	const hasLevels = competency.levels.length > 0;

	return (
		<div data-testid={`competency-row-${index}`} className="flex w-full items-center gap-2 p-2">
			<div className="flex w-[244px] shrink-0 items-center gap-2">
				<Text size="paragraph-3" weight="regular" color="neutral-9" dangerouslySetClassName="min-w-0">
					<span className="block py-text-truncate">{competency.name}</span>
				</Text>
				{competency.is_company_wide && (
					<Tooltip content="Company Wide Competency">
						<span
							data-testid={`company-wide-icon-${index}`}
							className="flex size-4 shrink-0 items-center justify-center"
						>
							<FontAwesomeIcon icon={faGlobe} className="text-sm text-primary-5" />
						</span>
					</Tooltip>
				)}
			</div>

			<div className="w-[232px] shrink-0">
				{hasLevels ? (
					<SingleSelectInput<number>
						value={rankToSelectValue(expectedLevel)}
						options={levelSelectOptions(competency.levels, NO_EXPECTED_LEVEL_LABEL)}
						onChange={(v) => onChangeLevel(selectValueToRank(v))}
					/>
				) : (
					<SingleSelectInput<number>
						value={undefined}
						options={[]}
						onChange={() => undefined}
						placeholder="No levels created"
						disabled
					/>
				)}
			</div>
			{/* Fixed right-anchored action column, kept even on company-wide rows (which can't be
			    removed) so every row's right edge lines up. */}
			<div className="flex-1" />
			<div className="flex w-8 shrink-0 items-center justify-center">
				{!competency.is_company_wide && (
					<ButtonIcon
						variant="standard"
						color="neutral"
						shade={7}
						size={16}
						icon={faTrashCan}
						aria-label={`Remove ${competency.name}`}
						data-testid={`remove-competency-${index}`}
						onClick={onRemove}
					/>
				)}
			</div>
		</div>
	);
};

export default AddCompetencyRow;
